import React, { FC } from 'react';
import CheckCircle from '@atlaskit/icon/glyph/check-circle';
import Tooltip from '@atlaskit/tooltip';
import CrossCircle from '@atlaskit/icon/glyph/cross-circle';
import JiraFailedBuildStatusIcon from '@atlaskit/icon/glyph/jira/failed-build-status';
import { token } from '@atlaskit/tokens';
import {
  SearchResultDTOTypeEnum,
  SloDTO,
  SloDTOFromJSON,
  CapabilityDTOFromJSON,
  CapabilityDTO,
} from '@atlassiansox/phobos-client-ts';

import { determineSloStatus } from '@/components/composed/slo-card/utils';
import { colors, MEET_COLOR, BREACH_COLOR } from '@/styles';
import {
  CapabilityEntityLink,
  ProductEntityLink,
  SLOEntityLink,
  TeamEntityLink,
  CapabilityTier,
  SLOGroupEntityLink,
} from '@/components/primitive';
import SloTypeLozenge from '@/components/composed/slo-card/lozenges/slo-type-lozenge';

import { SearchResultItemType } from '../types';
import { testIds } from '../../constants';

import * as styles from './styles';
import SearchKeyValueTag from './search-key-value-tag';

type SearchListNameProps = {
  item: SearchResultItemType;
  type: SearchResultDTOTypeEnum;
  isComfortView?: boolean;
};

const SearchListName: FC<SearchListNameProps> = ({ item, type, isComfortView }) => {
  const { name, publicId } = item;

  let displayElement;

  switch (type) {
    case SearchResultDTOTypeEnum.SloGroup: {
      const slo: SloDTO = SloDTOFromJSON(item);
      const { parent, indicator } = slo;

      displayElement = (
        <>
          <div css={styles.nameSLOTitleStyles}>
            <SLOGroupEntityLink hideTooltip name={name} publicId={publicId} indicator={indicator} />
          </div>
          {isComfortView && (
            <div css={styles.nameDetailsStyles}>
              <SloTypeLozenge type={slo.type} isCompact={false} />
              <SearchKeyValueTag title="capability" css={styles.nameKeyValueStyles}>
                <CapabilityEntityLink publicId={parent.publicId} name={parent.name} hideTooltip />
              </SearchKeyValueTag>
            </div>
          )}
        </>
      );
      break;
    }

    case SearchResultDTOTypeEnum.Slo: {
      let iconElement;
      const slo: SloDTO = SloDTOFromJSON(item);
      const { performance, objective, parent } = slo;

      switch (determineSloStatus({ performance, objective })) {
        case 'meets': {
          iconElement = (
            <Tooltip content={`This SLO meets the Objective of ${objective}%.`}>
              <CheckCircle
                testId={testIds.searchResultListTitleMeetIcon}
                label="SLO Meets"
                primaryColor={MEET_COLOR}
                size="small"
              />
            </Tooltip>
          );
          break;
        }
        case 'breaches': {
          iconElement = (
            <Tooltip content={`This SLO is currently breaching ${objective}%.`}>
              <CrossCircle
                testId={testIds.searchResultListTitleBreachIcon}
                label="SLO Breaches"
                primaryColor={BREACH_COLOR}
                size="small"
              />
            </Tooltip>
          );
          break;
        }
        default: {
          iconElement = (
            <Tooltip content="Something might be wrong with this SLO">
              <JiraFailedBuildStatusIcon
                testId={testIds.searchResultListTitleIssueIcon}
                label="Something might be wrong with this SLO"
                primaryColor={token('color.text.subtlest', colors.N100)}
                size="small"
              />
            </Tooltip>
          );
        }
      }

      displayElement = (
        <>
          <div css={styles.nameSLOTitleStyles}>
            {iconElement}
            <SLOEntityLink hideTooltip name={name} publicId={publicId} />
          </div>
          {isComfortView && (
            <div css={styles.nameDetailsStyles}>
              <SloTypeLozenge type={slo.type} isCompact={false} />
              <SearchKeyValueTag title="capability" css={styles.nameKeyValueStyles}>
                <CapabilityEntityLink publicId={parent.publicId} name={parent.name} hideTooltip />
              </SearchKeyValueTag>
            </div>
          )}
        </>
      );
      break;
    }
    case SearchResultDTOTypeEnum.Capability: {
      const capability: CapabilityDTO = CapabilityDTOFromJSON(item);
      const { team, product, importance } = capability;

      displayElement = (
        <>
          <CapabilityEntityLink hideTooltip name={name} publicId={publicId} />
          {isComfortView && (
            <div css={styles.nameDetailsStyles}>
              <SearchKeyValueTag title="capability tier">
                <CapabilityTier tier={importance} />
              </SearchKeyValueTag>
              <SearchKeyValueTag title="product" css={styles.nameKeyValueStyles}>
                <ProductEntityLink
                  hideTooltip
                  locationSearch="?showDrafts=true"
                  name={product.name}
                  publicId={product.publicId}
                />
              </SearchKeyValueTag>
              <SearchKeyValueTag title="team" css={styles.nameKeyValueStyles}>
                <TeamEntityLink
                  hideTooltip
                  locationSearch="?showDrafts=true"
                  name={team.name}
                  publicId={team.publicId}
                />
              </SearchKeyValueTag>
            </div>
          )}
        </>
      );
      break;
    }
    case SearchResultDTOTypeEnum.Team: {
      displayElement = (
        <TeamEntityLink
          hideTooltip
          locationSearch="?showDrafts=true"
          name={name}
          publicId={publicId}
        />
      );
      break;
    }
    case SearchResultDTOTypeEnum.Product: {
      displayElement = (
        <ProductEntityLink
          hideTooltip
          locationSearch="?showDrafts=true"
          name={name}
          publicId={publicId}
        />
      );
      break;
    }
  }

  return (
    <div css={styles.searchResultColumnStyles}>
      <div css={styles.cardTitleStyles}>{displayElement}</div>
    </div>
  );
};

export default SearchListName;
